import { PeriodFilter } from "@/components/date-filter/PeriodFilter.tsx";
import {
  ApiDateFilterMode,
  serializeDateFilter,
  parseDateFilterParam,
} from "@/components/date-filter/date-filter.type.ts";
import { FilterEmployeeRender } from "@/components/filter/renders/FilterEmployeeRender.tsx";
import { AppTypedFilters, FilterType, FilterTypeValue } from "@/components/filter/filter.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useEmploymentContractsSearchInline } from "@/queries/employment-contracts.query.ts";
import { useEmploymentTypesSearchInline } from "@/queries/employment-types.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsByIds, useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLocationsByIds, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useJobRolesSearchInline } from "@/queries/use-job-roles.query.ts";
import { useLegalEntitiesSearchInline } from "@/queries/use-legal-entities.query.ts";
import { useTeamSearchInline } from "@/queries/use-teams.query.ts";
import { getLifecycleStatusSelectItems } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { FilterItem, Multiselect, SelectItem } from "@skailer-com/uikit";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const useAppFilterEmployeeRenders = ({
  control,
  typedFilters,
  errors,
  filters,
}: {
  control: Control;
  typedFilters: AppTypedFilters;
  errors: FieldErrors;
  filters: FilterItem<FilterTypeValue>[];
}) => {
  const { t } = useTranslation();
  const { canCurrentEmployeeViewField, canCurrentEmployeeEditField, canIEdit, canIView } =
    usePermissions();

  const employeeFilters = filters.filter((item) => item.type === FilterType.employee);
  const canViewEmployees =
    employeeFilters.length > 0 &&
    (canIView(ApiPermission.employees_search) || canIView(ApiPermission.see_employee_directory));

  const canViewJobProfiles =
    filters.some((item) => item.type === FilterType.employeeJobProfile) &&
    canIView(ApiPermission.settings_positions_levels) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_position_job_title);
  const { options: jobProfileOptions, isLoading: isJobProfilesLoading } = useJobProfiles(
    false,
    canViewJobProfiles
  );
  const canViewJobRoles =
    filters.some((item) => item.type === FilterType.employeeJobRole) &&
    canIView(ApiPermission.settings_positions_levels) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_position_job_title);
  const jobRoles = useJobRolesSearchInline(typedFilters.job_role_ids || [], canViewJobRoles);

  const canViewDivision =
    filters.some((item) => item.type === FilterType.employeeDivision) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_position_division);
  const divisions = useDivisionsSearchInline(canViewDivision);
  const { data: divisionsLoaded } = useDivisionsByIds({
    ids: typedFilters.division_ids || [],
    enabled: canViewDivision,
  });

  const canViewDepartments =
    filters.some((item) => item.type === FilterType.employeeDepartment) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_position_department);
  const departmentsAll = useDepartmentsTree(canViewDepartments);

  const canViewLocations =
    filters.some((item) => item.type === FilterType.employeeLocation) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_position_location);
  const locations = useLocationsSearchInline(canViewLocations);
  const { data: locationsLoaded } = useLocationsByIds({
    ids: typedFilters.location_ids || [],
    enabled: canViewLocations,
  });

  const canViewTeams =
    filters.some((item) => item.type === FilterType.employeeTeam) &&
    canIView(ApiPermission.manage_teams) &&
    canIView(ApiPermission.see_employee_directory);
  const teams = useTeamSearchInline(canViewTeams);

  const canViewLifecycleStatus =
    filters.some((item) => item.type === FilterType.employeeLifecycleStatus) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_lifecycle_status);

  const canViewHiredOn =
    filters.some((item) => item.type === FilterType.employeeHiredOn) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_hired_on);

  const hasBirthdayFilter = filters.some((item) => item.type === FilterType.employeeBirthday);

  const canViewTerminationDate =
    filters.some((item) => item.type === FilterType.employeeTerminationDate) &&
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_termination_date);

  const canEditEmployment = canCurrentEmployeeEditField(
    EmployeeFieldPermission.manage_employee_employment
  );

  const canViewEmploymentTypes =
    filters.some((item) => item.type === FilterType.employmentType) &&
    (canIEdit(ApiPermission.settings_employment_types) ||
      canEditEmployment ||
      canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_employment_type));
  const employmentTypes = useEmploymentTypesSearchInline(canViewEmploymentTypes);

  const canViewEmploymentContracts =
    filters.some((item) => item.type === FilterType.employmentContract) &&
    (canIEdit(ApiPermission.settings_employment_contracts) ||
      canEditEmployment ||
      canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_contract));
  const employmentContracts = useEmploymentContractsSearchInline(canViewEmploymentContracts);

  const canViewLegalEntities =
    filters.some((item) => item.type === FilterType.employmentLegalEntity) &&
    (canIEdit(ApiPermission.settings_legal_entities) ||
      canEditEmployment ||
      canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_legal_entity));
  const legalEntities = useLegalEntitiesSearchInline(canViewLegalEntities);

  const renderFilterEmployee = (item: FilterItem<FilterTypeValue>) => (
    <FilterEmployeeRender
      item={item}
      control={control}
      errors={errors}
      typedFilters={typedFilters}
      canViewEmployees={canViewEmployees}
    />
  );

  const renderFilterJobProfiles = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewJobProfiles) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:job_profile")}
            options={jobProfileOptions}
            values={value}
            onChange={onChange}
            searchable
            isClearable
            showCaption
            captionPerItem
            error={errors[item.name]?.message as string}
            isLoading={isJobProfilesLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterJobRoles = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewJobRoles) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:job_role")}
            options={jobRoles.options}
            searchOptions={jobRoles.options}
            onSearchOptions={jobRoles.search}
            loadOptionsOnOpen
            values={value}
            onChange={onChange}
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={jobRoles.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterDivisions = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewDivision) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:division")}
            options={divisionsLoaded as SelectItem[]}
            values={value}
            onChange={onChange}
            searchOptions={divisions.options}
            onSearchOptions={divisions.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={divisions.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterDepartments = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewDepartments) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:department")}
            options={departmentsAll?.data}
            values={value}
            onChange={onChange}
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={departmentsAll.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterLocations = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewLocations) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("common:location")}
            options={locationsLoaded}
            values={value}
            onChange={onChange}
            searchOptions={locations.options}
            onSearchOptions={locations.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={locations.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterTeams = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewTeams) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:teams")}
            options={teams.options}
            values={value}
            onChange={onChange}
            searchOptions={teams.options}
            onSearchOptions={teams.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={teams.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterEmployeeLifecycle = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewLifecycleStatus) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <Multiselect
            label={t("core:lifecycle_status")}
            emptyTitle={t("common:select")}
            options={getLifecycleStatusSelectItems()}
            values={value}
            onChange={onChange}
            isClearable
            error={errors[item.name]?.message as string}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterEmploymentTypes = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewEmploymentTypes) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:employment_type")}
            options={employmentTypes.options}
            values={value}
            onChange={onChange}
            searchOptions={employmentTypes.options}
            onSearchOptions={employmentTypes.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={employmentTypes.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterEmploymentContracts = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewEmploymentContracts) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:employment_contract")}
            options={employmentContracts.options}
            values={value}
            onChange={onChange}
            searchOptions={employmentContracts.options}
            onSearchOptions={employmentContracts.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={employmentContracts.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterLegalEntities = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewLegalEntities) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:legal_entity")}
            options={legalEntities.options}
            values={value}
            onChange={onChange}
            searchOptions={legalEntities.options}
            onSearchOptions={legalEntities.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={legalEntities.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterHiredOn = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewHiredOn) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <PeriodFilter
            title={t("core:hired_on")}
            value={parseDateFilterParam(value)}
            availableModes={[ApiDateFilterMode.date]}
            onChange={(next) => onChange(serializeDateFilter(next) ?? null)}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterBirthday = (item: FilterItem<FilterTypeValue>) => {
    if (!hasBirthdayFilter) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <PeriodFilter
            title={t("core:birth_date")}
            value={parseDateFilterParam(value)}
            availableModes={[ApiDateFilterMode.day]}
            onChange={(next) => onChange(serializeDateFilter(next) ?? null)}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterTerminationDate = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewTerminationDate) return null;
    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <PeriodFilter
            title={t("core:termination_date")}
            value={parseDateFilterParam(value)}
            availableModes={[ApiDateFilterMode.date]}
            onChange={(next) => onChange(serializeDateFilter(next) ?? null)}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  return {
    renderFilterJobProfiles,
    renderFilterJobRoles,
    renderFilterDivisions,
    renderFilterDepartments,
    renderFilterLocations,
    renderFilterTeams,
    renderFilterEmployeeLifecycle,
    renderFilterEmployee,
    renderFilterEmploymentTypes,
    renderFilterEmploymentContracts,
    renderFilterLegalEntities,
    renderFilterHiredOn,
    renderFilterBirthday,
    renderFilterTerminationDate,
  };
};
